import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import CanonicalHead from "@/components/CanonicalHead";
import ProtectedRoute from "@/components/auth/ProtectedRoute";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ManagerDashboard = lazy(() => import("./pages/ManagerDashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminTours = lazy(() => import("./pages/AdminTours"));
const AdminPricingMaster = lazy(() => import("./pages/AdminPricingMaster"));
const AdminSpecialties = lazy(() => import("./pages/AdminSpecialties"));
const Destinations = lazy(() => import("./pages/Destinations"));
const OurSpecialties = lazy(() => import("./pages/OurSpecialties"));
const TourDetail = lazy(() => import("./pages/TourDetail"));
const TripDetailPage = lazy(() => import("./pages/TripDetailPage"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const TripConfirmation = lazy(() => import("./pages/TripConfirmation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const PlanMyTrip = lazy(() => import("./pages/PlanMyTrip"));
const WanakaWildwire = lazy(() => import("./components/WanakaWildwireDetail"));
const MouWahoIsland = lazy(() => import("./components/MouWahoIslandDetail"));
const DeepCanyon = lazy(() => import("./components/DeepCanyonDetail"));
const JetSkiTour = lazy(() => import("./components/JetSkiTourDetail"));
const HotAirBalloon = lazy(() => import("./components/HotAirBalloonDetail"));
const QueenstownBalloon = lazy(() => import("./pages/QueenstownBalloonPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div style={{ minHeight: '100vh', backgroundColor: 'hsl(0 0% 100%)' }} />}>
            <CanonicalHead />
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/destinations" element={<Destinations />} />
              <Route path="/specialties" element={<OurSpecialties />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/plan-my-trip" element={<PlanMyTrip />} />
              <Route path="/tour/:id" element={<TourDetail />} />
              <Route path="/tours/wanaka-wildwire" element={<WanakaWildwire />} />
              <Route path="/tours/wanaka-mou-waho-island" element={<MouWahoIsland />} />
              <Route path="/tours/wanaka-deep-canyon" element={<DeepCanyon />} />
              <Route path="/tours/wanaka-jet-ski-tour" element={<JetSkiTour />} />
              <Route path="/tours/wanaka-hot-air-balloon" element={<HotAirBalloon />} />
              <Route path="/tours/queenstown-hot-air-balloon" element={<QueenstownBalloon />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsConditions />} />

              {/* Authenticated customer routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/trip/:id" element={
                <ProtectedRoute>
                  <TripDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/payment-success" element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              } />
              <Route path="/trip-confirmation" element={
                <ProtectedRoute>
                  <TripConfirmation />
                </ProtectedRoute>
              } />

              {/* Manager routes */}
              <Route path="/manager" element={
                <ProtectedRoute requireRole="tour_manager">
                  <ManagerDashboard />
                </ProtectedRoute>
              } />

              {/* Admin-only routes */}
              <Route path="/admin" element={
                <ProtectedRoute requireRole="tour_admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/tours" element={
                <ProtectedRoute requireRole="tour_admin">
                  <AdminTours />
                </ProtectedRoute>
              } />
              <Route path="/admin/specialties" element={
                <ProtectedRoute requireRole="tour_admin">
                  <AdminSpecialties />
                </ProtectedRoute>
              } />
              <Route path="/admin/pricing" element={
                <ProtectedRoute requireRole="tour_admin">
                  <AdminPricingMaster />
                </ProtectedRoute>
              } />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
